import { X, Sparkles, Loader } from 'lucide-react';
import { useState } from 'react';

interface QuickObservationPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (observation: string, additionalContext: string, topic: string, actionType: 'IMPROVE' | 'SUSTAIN', sentiment: string, location?: string, recommendation?: string) => void;
}

export function QuickObservationPopup({ isOpen, onClose, onSubmit }: QuickObservationPopupProps) {
  const [observation, setObservation] = useState('');
  const [additionalContext, setAdditionalContext] = useState('');
  const [topic, setTopic] = useState('AI Bias or Risk');
  const [customTopic, setCustomTopic] = useState('');
  const [actionType, setActionType] = useState<'IMPROVE' | 'SUSTAIN' | null>(null);
  const [sentiment, setSentiment] = useState<string | null>(null);
  const [eventType, setEventType] = useState<'Event' | 'Routine' | null>(null);
  const [location, setLocation] = useState('Jacksonville, NC (Camp Lejeune)');
  const [recommendation, setRecommendation] = useState('');
  const [isGeneratingRecommendation, setIsGeneratingRecommendation] = useState(false);

  if (!isOpen) return null;

  const generateRecommendation = async () => {
    if (!observation.trim()) {
      alert('Please enter an observation first.');
      return;
    }

    setIsGeneratingRecommendation(true);
    try {
      const finalTopic = topic === 'Other' ? customTopic.trim() : topic;
      const prompt = `Based on the following observation and topic, provide a concise, actionable recommendation (max 300 characters):\n\nTopic: ${finalTopic}\nObservation: ${observation}\n\nRecommendation:`;

      const apiKey = typeof window !== 'undefined' ? window.location.search.includes('test') ? 'test-key' : '' : '';
      
      // Note: For production, the API call should be made through your backend
      // to avoid exposing the API key in the frontend
      const response = await fetch('/api/generateRecommendation', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          observation: observation.trim(),
          topic: finalTopic,
          sentiment: sentiment || 'neutral',
          type: eventType || 'Event',
          additionalContext: additionalContext.trim()
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        console.error('API Error:', {
          status: response.status,
          statusText: response.statusText,
          error: errorData
        });
        throw new Error(errorData.error || 'Failed to generate recommendation');
      }

      const data = await response.json();
      const generatedText = data.recommendation?.trim() || '';
      setRecommendation(generatedText.substring(0, 300));
    } catch (error) {
      console.error('Error generating recommendation:', error);
      alert('Unable to generate recommendation. Please try again.');
    } finally {
      setIsGeneratingRecommendation(false);
    }
  };

  const handleActionTypeClick = (type: 'IMPROVE' | 'SUSTAIN') => {
    setActionType(type);
    if (type === 'IMPROVE' && !recommendation && observation.trim()) {
      generateRecommendation();
    } else if (type === 'SUSTAIN') {
      setRecommendation('');
    }
  };

  const handleSubmit = () => {
    if (!observation.trim()) {
      alert('Please enter an observation before submitting.');
      return;
    }
    if (!eventType) {
      alert('Please select a Type (Event or Routine).');
      return;
    }
    if (eventType === 'Event' && !location.trim()) {
      alert('Please enter a location for this event.');
      return;
    }
    if (!actionType) {
      alert('Please select either IMPROVE or SUSTAIN.');
      return;
    }
    if (!sentiment) {
      alert('Please select how you are feeling about this.');
      return;
    }
    // Use custom topic if "Other" is selected
    const finalTopic = topic === 'Other' ? customTopic.trim() : topic;
    if (!finalTopic) {
      alert('Please specify a topic.');
      return;
    }
    onSubmit(observation, additionalContext, finalTopic, actionType, sentiment, location, recommendation);
    // Reset form
    setObservation('');
    setAdditionalContext('');
    setTopic('AI Bias or Risk');
    setCustomTopic('');
    setActionType(null);
    setSentiment(null);
    setEventType(null);
    setLocation('Jacksonville, NC (Camp Lejeune)');
    setRecommendation('');
  };

  const handleCancel = () => {
    setObservation('');
    setAdditionalContext('');
    setTopic('AI Bias or Risk');
    setCustomTopic('');
    setActionType(null);
    setSentiment(null);
    setEventType(null);
    setLocation('Jacksonville, NC (Camp Lejeune)');
    setRecommendation('');
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-[70] p-2">
      <div className="bg-white rounded-lg max-w-[368px] w-full shadow-2xl max-h-[90vh] flex flex-col">
        {/* Header with Close Button */}
        <div className="p-3 pb-1 relative flex-shrink-0">
          <button
            onClick={handleCancel}
            className="absolute top-2 right-2 text-gray-600 hover:text-gray-900 p-1 rounded transition-colors"
            title="Close"
          >
            <X className="size-5" />
          </button>
          <h2 className="text-base font-bold text-black">Quick Observation</h2>
        </div>

        {/* Body - Scrollable */}
        <div className="px-3 pb-2 space-y-1.5 overflow-y-auto flex-1">
          {/* AI Bias or Risk Header */}
          <div>
            <h3 className="text-xs font-semibold text-black">Quick Observation</h3>
            <p className="text-[11px] text-black text-center py-0.5">
              Tell us how your team is using agentic AI
            </p>
          </div>

          {/* Topic Selection Dropdown */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-0.5">
              Topic: *
            </label>
            <select
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              title="Select a topic for your observation"
              className="w-full px-2 py-1.5 bg-white border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-xs text-black"
            >
              <option value="AI Bias or Risk">AI Bias or Risk</option>
              <option value="Information & Intelligence">Information & Intelligence</option>
              <option value="Human Health & Fitness">Human Health & Fitness</option>
              <option value="Training & Development">Training & Development</option>
              <option value="Governance & Compliance">Governance & Compliance</option>
              <option value="Other">Other</option>
            </select>
          </div>

          {/* Other Topic Input - Only shown when Other is selected */}
          {topic === "Other" && (
            <div>
              <label className="block text-[11px] font-semibold text-black mb-0.5">
                Please specify your topic:
              </label>
              <input
                type="text"
                value={customTopic}
                onChange={(e) => setCustomTopic(e.target.value)}
                placeholder="Enter your custom topic..."
                className="w-full px-2 py-1.5 bg-white border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-xs text-black placeholder-gray-400"
              />
            </div>
          )}

          {/* Observation or Issue */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-0.5">
              Observation or Issue:
            </label>
            <textarea
              value={observation}
              onChange={(e) => setObservation(e.target.value)}
              placeholder="Tell us how your are using the agent and what needs improvement!"
              className="w-full px-2 py-1.5 bg-white border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-xs text-black placeholder-gray-400 resize-none h-16"
              required
            />
          </div>

          {/* Additional Context */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-0.5">
              Additional Context (Optional):
            </label>
            <textarea
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Add any additional details or context here..."
              className="w-full px-2 py-1.5 bg-white border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-xs text-black placeholder-gray-400 resize-none h-14"
            />
          </div>

          {/* Event Type Selection - Shown when Event is selected */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-0.5">
              Type: *
            </label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => setEventType('Event')}
                className={`flex-1 px-2 py-1 text-xs font-semibold rounded transition-colors ${
                  eventType === 'Event'
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-black'
                }`}
              >
                Event
              </button>
              <button
                type="button"
                onClick={() => setEventType('Routine')}
                className={`flex-1 px-2 py-1 text-xs font-semibold rounded transition-colors ${
                  eventType === 'Routine'
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-black'
                }`}
              >
                Routine
              </button>
            </div>
          </div>

          {/* Location Field - Only shown when Event is selected */}
          {eventType === 'Event' && (
            <div>
              <label className="block text-[11px] font-semibold text-black mb-0.5">
                Location:
              </label>
              <input
                type="text"
                value={location}
                onChange={(e) => setLocation(e.target.value)}
                placeholder="Enter location (e.g., Camp Lejeune, NC)"
                className="w-full px-2 py-1.5 bg-white border-2 border-gray-300 rounded-lg focus:border-purple-500 focus:outline-none text-xs text-black placeholder-gray-400"
              />
            </div>
          )}

          {/* Action Type Selection */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-0.5">
              Action Type: *
            </label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={() => handleActionTypeClick('IMPROVE')}
                className={`flex-1 px-2 py-1 text-xs font-semibold rounded transition-colors ${
                  actionType === 'IMPROVE'
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-black'
                }`}
              >
                {isGeneratingRecommendation && actionType === 'IMPROVE' ? (
                  <span className="inline-flex items-center gap-1">
                    <span className="inline-block animate-spin">⚙️</span> Generating...
                  </span>
                ) : (
                  'IMPROVE'
                )}
              </button>
              <button
                type="button"
                onClick={() => handleActionTypeClick('SUSTAIN')}
                className={`flex-1 px-2 py-1 text-xs font-semibold rounded transition-colors ${
                  actionType === 'SUSTAIN'
                    ? 'bg-purple-600 hover:bg-purple-700 text-white'
                    : 'bg-gray-200 hover:bg-gray-300 text-black'
                }`}
              >
                SUSTAIN
              </button>
            </div>
          </div>

          {/* Recommendation Field - Only shown when IMPROVE is selected */}
          {actionType === 'IMPROVE' && (
            <div>
              <label className="block text-[11px] font-semibold text-black mb-0.5">
                Recommendation (AI Generated):
              </label>
              <textarea
                value={recommendation}
                onChange={(e) => setRecommendation(e.target.value.substring(0, 300))}
                placeholder="AI will generate a recommendation based on your observation..."
                maxLength={300}
                rows={3}
                className="w-full px-2 py-1.5 bg-white border-2 border-purple-300 rounded-lg focus:border-purple-500 focus:outline-none text-xs text-black placeholder-gray-400 resize-none"
              />
              <p className="text-[9px] text-gray-600 mt-0.5">
                {recommendation.length}/300 characters
              </p>
            </div>
          )}

          {/* Sentiment Selection */}
          <div>
            <label className="block text-[11px] font-semibold text-black mb-0.5">
              How are you feeling about this? *
            </label>
            <div className="grid grid-cols-4 gap-1">
              {[
                { emoji: "😠", label: "Angry" },
                { emoji: "😕", label: "Confused" },
                { emoji: "🤔", label: "Curious" },
                { emoji: "😊", label: "Happy" },
                { emoji: "😢", label: "Sad" },
                { emoji: "😤", label: "Frustrated" },
                { emoji: "😐", label: "Meh" },
                { emoji: "😌", label: "Satisfied" }
              ].map((item) => (
                <button
                  key={item.label}
                  type="button"
                  onClick={() => setSentiment(item.label)}
                  className={`flex flex-col items-center justify-center px-0.5 py-0.5 rounded-lg border-2 transition-all ${
                    sentiment === item.label
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-300 bg-white hover:border-gray-400'
                  }`}
                >
                  <span className="text-base">{item.emoji}</span>
                  <span className="text-[7px] text-black font-medium leading-tight">{item.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-1.5 pt-1">
            <button
              onClick={handleCancel}
              className="flex-1 px-2 py-1.5 bg-gray-200 hover:bg-gray-300 text-black text-xs font-semibold rounded transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              className="flex-1 px-2 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-semibold rounded transition-colors"
            >
              Send
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}